import { ApolloClient, useApolloClient } from '@apollo/client';
import { Button, Checkbox, Icon, Row, Size, Theme } from '@busybusy/webapp-react-ui';
import { DownloadIcon } from 'assets/icons';
import classNames from 'classnames';
import { Thumbnail } from 'components';
import { isNil } from 'lodash';
import * as React from 'react';
import { useSelector } from 'react-redux';
import { IStore } from 'store/types';
import { IMediaCollection } from 'types/MediaCollection';
import { IMediaEntry, MediaEntryType } from 'types/MediaEntry';
import PhotoUtilities, { getPhotoThumbnailUrl } from '../photo-utilities/photo-utilities';
import './MediaEntry.scss';
import ProcessingPhoto from './ProcessingPhoto/ProcessingPhoto';

export interface IMediaEntryContainerProps {
  className?: string | object;
  checkable: boolean;
  mediaEntry: IMediaEntry;
  clickable: boolean;
  onCheck?: (entry: IMediaEntry, isChecked: boolean) => void;
  checked?: boolean;
  onMediaEntryClicked?: (entry: IMediaEntry) => void;
  showDock?: boolean;
}

export interface IMediaEntryProps extends Omit<IMediaEntryContainerProps, 'isShifted'> {
  apolloClient: ApolloClient<any>;
  mediaCollections: IMediaCollection[];
}

export const MediaEntryContainer: React.FC<IMediaEntryContainerProps> = (props) => {
  const { ...rest } = props;
  const apolloClient = useApolloClient();
  const mediaCollections = useSelector<IStore, IMediaCollection[]>((state) => state.photos.mediaCollections);

  return <MediaEntry {...rest} apolloClient={apolloClient} mediaCollections={mediaCollections} />;
};

export const MediaEntry: React.FC<IMediaEntryProps> = (props) => {
  const {
    checked,
    checkable,
    clickable,
    onMediaEntryClicked,
    mediaEntry,
    mediaCollections,
    onCheck,
    className,
    apolloClient,
    showDock,
  } = props;

  const classes = classNames({ 'media-entry': true }, className);

  const encompassingCollection = PhotoUtilities.getCollectionForMediaFromCollections(mediaCollections, mediaEntry)!;

  function handleOnClick() {
    if (clickable && onMediaEntryClicked) {
      onMediaEntryClicked(mediaEntry);
    }
  }

  function handleOnCheck(isChecked: boolean) {
    if (checkable && onCheck) {
      onCheck(mediaEntry, isChecked);
    }
  }

  function handleDownload() {
    return PhotoUtilities.loadMediaEntry(apolloClient, mediaEntry.id).then((freshMediaEntries) => {
      const entryCollection = { encompassingCollection, mediaEntry: freshMediaEntries.shift()! };

      return PhotoUtilities.downloadMediaEntry(entryCollection);
    });
  }

  function renderDockTemplate() {
    return (
      <Row className="p-1">
        <Button size={Size.SMALL} theme={Theme.DARK} type="icon" onClick={handleDownload}>
          <Icon svg={DownloadIcon} />
        </Button>
      </Row>
    );
  }

  function stopPropagation(e: React.MouseEvent) {
    e.stopPropagation();
    e.nativeEvent.stopPropagation();
  }

  return (
    <div className={classes}>
      {mediaEntry.entryType === MediaEntryType.IMAGE && (
        <>
          {isNil(mediaEntry.fileUrl) ? (
            <ProcessingPhoto />
          ) : (
            <Thumbnail
              className={classes}
              key={mediaEntry.id}
              fileUrl={getPhotoThumbnailUrl(mediaEntry.fileUrl)}
              onClick={handleOnClick}
              dockTemplate={showDock === false ? undefined : renderDockTemplate()}
            >
              {checkable && checked !== undefined && (
                <Checkbox className="m-1" onChange={handleOnCheck} checked={checked} onClick={stopPropagation} />
              )}
            </Thumbnail>
          )}
        </>
      )}
    </div>
  );
};

export default MediaEntryContainer;
